import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
  CategoriesProps,
  determineColor,
} from '../../Benefits/BenefitsPreview/GroupedBenefits';
import { PSBannerWrapper } from 'enrollment/components/Shared';
import { GroupedBenefitsV2 } from '../BenefitsPreviewV2/GroupedBenefitsV2';
import { BenefitsPreviewCategoryContainerV2 } from './CategoriesV2ContainerStyled';
import { CategoriesV2Container } from './CategoriesV2Container';
import {
  BundledBenefitsCategories,
  ChosenBundleTopContent,
} from './BundledBenefitsCategories';
import {
  hasSavviBenefits,
  useSavviOverRideChosenBenefits,
} from 'enrollment/utils/bundledBenefitsUtils';

export interface ReloadingCategoryState {
  state: {
    fromCategory?: string;
  };
}

export interface CategoriesV2Props extends CategoriesProps {
  allCategories: string[];
  onBenefitMount?: (id: string) => void;
}

export const CategoriesV2 = ({
  benefitCategories,
  benefitCategoryData,
  currentBenefitCategory,
  benefitCategoryNames,
  isLoading,
  allCategories,
  onBenefitMount,
}: CategoriesV2Props) => {
  const { t } = useTranslation('messages.notEligible');
  const location = useLocation() as ReloadingCategoryState;

  const isLast = benefitCategories.length === allCategories.length;
  const fromCategory = location.state?.fromCategory;

  const savviIsEnabledForContext = useSavviOverRideChosenBenefits();
  const savviOverRideChosenBenefits =
    savviIsEnabledForContext &&
    hasSavviBenefits(
      benefitCategoryData.data.benefits,
      benefitCategoryData?.data.bundled_org_benefit_ids
    );

  if (savviOverRideChosenBenefits) {
    return (
      <BundledBenefitsCategories
        benefitCategoryData={benefitCategoryData}
        isLoading={isLoading}
      />
    );
  }

  return (
    <div>
      {savviIsEnabledForContext && (
        <ChosenBundleTopContent
          buttonType="primary"
          buttonLabel="exploreBundlesButton"
          header="noChosenBundleHeader"
          description="noChosenBundleDescription"
          solidBackground={true}
        />
      )}
      {benefitCategories.map((benefitCategory, index) => {
        const color = determineColor(benefitCategories[index]);
        const isBottom = index === benefitCategories.length - 1;
        const benefitGroupName = benefitCategoryNames[index];
        const benefitPreviews = GroupedBenefitsV2({
          benefitCategoryData,
          benefitCategory,
          currentBenefitCategory,
          color,
          loading: isLoading,
          onBenefitMount,
        });

        const sortedBenefitPreviews = benefitPreviews.sort((a, b) => {
          const orderA = a?.props.benefitData.benefit.display_order || 0;
          const orderB = b?.props.benefitData.benefit.display_order || 0;

          return orderA - orderB;
        });

        const reloadingCategory = fromCategory === benefitCategory;
        const hasEligibleBenefit = sortedBenefitPreviews.find(
          (benefit) => benefit
        );
        const categoryTotal = benefitCategoryData.data.benefits.reduce(
          (acc, benefit) => {
            let val = 0;
            if (
              benefit.benefit.org_benefit_category === benefitCategory &&
              benefit.coverage
            ) {
              val += Number(benefit.coverage.req_subscriber_premium);
              benefit.coverage.rider_coverages?.forEach((rider) => {
                val += Number(rider.req_subscriber_premium);
              });
              benefit.coverage.coverage_lines?.forEach((line) => {
                line.rider_coverage_lines.forEach((rider) => {
                  val += Number(rider.req_subscriber_premium);
                });
              });
            }
            return acc + val;
          },
          0
        );

        if (hasEligibleBenefit) {
          return (
            <div key={index}>
              <CategoriesV2Container
                key={index}
                id={benefitGroupName}
                groupName={benefitGroupName}
                dataTestId="benefit-grouping-container"
                loading={
                  isLoading && (!hasEligibleBenefit || reloadingCategory)
                }
                className="category-container"
                categoryTotal={categoryTotal}
                color={color}
              >
                <BenefitsPreviewCategoryContainerV2
                  id="preview-container"
                  className="m-0"
                >
                  {benefitPreviews}
                </BenefitsPreviewCategoryContainerV2>
              </CategoriesV2Container>
              {!isLast && isBottom && (
                <PSBannerWrapper
                  id="not-current-cateogry-banner"
                  message={t('moreBenefits')}
                  level="info"
                  icon
                />
              )}
            </div>
          );
        } else {
          return <Fragment key={index}></Fragment>;
        }
      })}
    </div>
  );
};
